import { ArrowDown, ArrowUp, ArrowUpDown, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import Paginacion from '../../ui/Paginacion'
import { usePaginacion } from '../../../hooks/usePaginacion'
import { formatearNumero, formatearVariacionPct } from '../../../utils/analiticaPos'
import {
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
} from '../../../utils/pos'
import { GraficoRankingHorizontal } from './GraficoBarras'
import { estiloInputBusqueda } from '../FiltrosPosComunes'

const RANKINGS = [
  { valor: 'total_comprado', etiqueta: 'Top 10 por monto comprado', formato: 'monto', orden: 'desc' },
  { valor: 'kilos_comprados', etiqueta: 'Top 10 por kilos comprados', formato: 'kilos', orden: 'desc' },
  { valor: 'frecuencia_promedio_dias', etiqueta: 'Top 10 por frecuencia de compra', formato: 'dias', orden: 'asc', requiereNoNulo: true },
  { valor: 'ticket_promedio', etiqueta: 'Top 10 por ticket promedio', formato: 'monto', orden: 'desc' },
  { valor: 'saldo_pendiente', etiqueta: 'Top 10 por saldo pendiente', formato: 'monto', orden: 'desc' },
  { valor: 'productos_distintos', etiqueta: 'Top 10 por productos distintos', formato: 'numero', orden: 'desc' },
  { valor: 'variacion_pct', etiqueta: 'Top 10 mayor crecimiento vs. período anterior', formato: 'pct', orden: 'desc', filtrarComparables: true },
  { valor: 'variacion_pct_caida', etiqueta: 'Top 10 mayor caída vs. período anterior', formato: 'pct', orden: 'asc', filtrarComparables: true, clave: 'variacion_pct' },
]

const COLUMNAS = [
  { clave: 'razon_social', etiqueta: 'Cliente' },
  { clave: 'cantidad_compras', etiqueta: 'Compras' },
  { clave: 'kilos_comprados', etiqueta: 'Kg' },
  { clave: 'total_comprado', etiqueta: 'Total comprado' },
  { clave: 'total_pagado', etiqueta: 'Pagado' },
  { clave: 'saldo_pendiente', etiqueta: 'Saldo' },
  { clave: 'ticket_promedio', etiqueta: 'Ticket prom.' },
  { clave: 'productos_distintos', etiqueta: 'Productos' },
  { clave: 'ultima_compra_historica', etiqueta: 'Última compra' },
  { clave: 'dias_desde_ultima_compra', etiqueta: 'Días sin comprar' },
]

function formatearValorRanking(valor, formato) {
  if (formato === 'monto') return formatearMontoPos(valor)
  if (formato === 'kilos') return formatearKilosPos(valor)
  if (formato === 'pct') return formatearVariacionPct(valor) || '—'
  if (formato === 'dias') return `${formatearNumero(valor, 1)} días`
  return String(Math.round(valor))
}

function ClientesAnalitica({ clientes, onSeleccionarCliente, clienteSeleccionado }) {
  const [busqueda, setBusqueda] = useState('')
  const [ordenPor, setOrdenPor] = useState('total_comprado')
  const [ordenAsc, setOrdenAsc] = useState(false)
  const [rankingActivo, setRankingActivo] = useState('total_comprado')

  const terminoBusqueda = busqueda.trim().toLowerCase()

  const filtrados = useMemo(() => {
    if (!terminoBusqueda) return clientes
    return clientes.filter((c) => c.razon_social?.toLowerCase().includes(terminoBusqueda))
  }, [clientes, terminoBusqueda])

  const ordenados = useMemo(() => {
    return [...filtrados].sort((a, b) => {
      const valorA = a[ordenPor]
      const valorB = b[ordenPor]

      if (valorA === valorB) return 0
      if (valorA === null || valorA === undefined) return 1
      if (valorB === null || valorB === undefined) return -1

      const comparacion = typeof valorA === 'string' ? valorA.localeCompare(valorB) : valorA - valorB

      return ordenAsc ? comparacion : -comparacion
    })
  }, [filtrados, ordenPor, ordenAsc])

  const filtrosKey = JSON.stringify([terminoBusqueda])
  const { pagina, setPagina, tamanioPagina, setTamanioPagina, totalPaginas, indiceInicio, indiceFin } =
    usePaginacion({ totalRegistros: ordenados.length, filtrosKey })

  const paginados = useMemo(() => {
    const inicio = (pagina - 1) * tamanioPagina
    return ordenados.slice(inicio, inicio + tamanioPagina)
  }, [ordenados, pagina, tamanioPagina])

  const alternarOrden = (clave) => {
    if (ordenPor === clave) {
      setOrdenAsc((actual) => !actual)
    } else {
      setOrdenPor(clave)
      setOrdenAsc(false)
    }
  }

  const configRanking = RANKINGS.find((r) => r.valor === rankingActivo) || RANKINGS[0]

  const datosRanking = useMemo(() => {
    const clave = configRanking.clave || configRanking.valor
    let base = clientes.filter((c) => c[clave] !== null && c[clave] !== undefined)

    if (configRanking.requiereNoNulo) {
      base = base.filter((c) => c.cantidad_compras > 1)
    }
    if (configRanking.filtrarComparables) {
      base = base.filter((c) => c.monto_periodo_anterior !== null && c.monto_periodo_anterior > 0)
    }

    const direccion = configRanking.orden === 'asc' ? 1 : -1

    return base
      .sort((a, b) => (a[clave] - b[clave]) * direccion)
      .slice(0, 10)
      .map((c) => ({ etiqueta: c.razon_social, valor: c[clave] }))
  }, [clientes, configRanking])

  if (clientes.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <p className="text-slate-500">No hay clientes con compras para los filtros seleccionados.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-bold text-slate-900">Rankings de clientes</h3>

          <select
            value={rankingActivo}
            onChange={(event) => setRankingActivo(event.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          >
            {RANKINGS.map((r) => (
              <option key={r.etiqueta} value={r.valor}>
                {r.etiqueta}
              </option>
            ))}
          </select>
        </div>

        {datosRanking.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            {configRanking.filtrarComparables
              ? 'Sin período anterior comparable para este ranking.'
              : 'Sin datos.'}
          </p>
        ) : (
          <GraficoRankingHorizontal
            datos={datosRanking}
            formatearValor={(valor) => formatearValorRanking(valor, configRanking.formato)}
            color="bg-sky-500"
          />
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-bold text-slate-900">Detalle por cliente</h3>

          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar cliente..."
              className={estiloInputBusqueda}
            />
          </div>
        </div>

        {clienteSeleccionado && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
            Cliente seleccionado para desglose de productos: {clienteSeleccionado.razon_social}
            <button type="button" onClick={() => onSeleccionarCliente(null)} className="text-emerald-600 hover:text-emerald-800">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="w-full overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                {COLUMNAS.map((col) => (
                  <th key={col.clave} className="whitespace-nowrap px-3 py-2">
                    <button
                      type="button"
                      onClick={() => alternarOrden(col.clave)}
                      className="flex items-center gap-1 hover:text-slate-800"
                    >
                      {col.etiqueta}
                      {ordenPor === col.clave ? (
                        ordenAsc ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} className="text-slate-300" />
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {paginados.map((c) => (
                <tr
                  key={c.cliente_id}
                  onClick={() => onSeleccionarCliente(c)}
                  className={`cursor-pointer transition hover:bg-slate-50 ${
                    clienteSeleccionado?.cliente_id === c.cliente_id ? 'bg-emerald-50' : ''
                  }`}
                >
                  <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-900">
                    {c.razon_social}
                    <div className="flex gap-1.5">
                      {c.cliente_nuevo && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Nuevo</span>
                      )}
                      {c.cliente_recurrente && (
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">Recurrente</span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{c.cantidad_compras}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearKilosPos(c.kilos_comprados)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-slate-900">{formatearMontoPos(c.total_comprado)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearMontoPos(c.total_pagado)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearMontoPos(c.saldo_pendiente)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearMontoPos(c.ticket_promedio)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{c.productos_distintos}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{formatearFechaPos(c.ultima_compra_historica)}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">{c.dias_desde_ultima_compra}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Paginacion
          pagina={pagina}
          totalPaginas={totalPaginas}
          tamanioPagina={tamanioPagina}
          totalRegistros={ordenados.length}
          indiceInicio={indiceInicio}
          indiceFin={indiceFin}
          etiquetaRegistros="clientes"
          onCambiarPagina={setPagina}
          onCambiarTamanio={setTamanioPagina}
        />
      </div>
    </div>
  )
}

export default ClientesAnalitica
